"use client"

import { useEffect } from "react"
import { useRouter } from "next/navigation"
import { useSystemConfiguration } from "./system-configuration-provider"
import type { FeatureFlagKey } from "@/shared/schema/feature-flags"
import type { UseFeatureFlagOptions, UseFeatureFlagReturn } from "./types"

/**
 * Hook to check if a specific feature is enabled.
 *
 * @example Basic usage
 * ```tsx
 * const { isEnabled, isLoading } = useFeatureFlag("cova_match")
 *
 * if (isLoading) return <Skeleton />
 * if (!isEnabled) return null
 *
 * return <CovaMatchFeature />
 * ```
 *
 * @example With automatic redirect when disabled
 * ```tsx
 * const { isEnabled, isLoading } = useFeatureFlag("cova_match", {
 *   redirectIfDisabled: "/assistants"
 * })
 *
 * // Show loading while checking flag or redirecting
 * if (isLoading || !isEnabled) return <AppLoading />
 *
 * return <CovaMatchFeature />
 * ```
 */
export function useFeatureFlag(
  key: FeatureFlagKey,
  options?: UseFeatureFlagOptions
): UseFeatureFlagReturn {
  const router = useRouter()
  const { getFeatureState, isLoading } = useSystemConfiguration()

  const state = getFeatureState(key)
  const isEnabled = state?.enabled ?? false

  // Handle automatic redirect when feature is disabled
  useEffect(() => {
    if (options?.redirectIfDisabled && !isLoading && !isEnabled) {
      router.replace(options.redirectIfDisabled)
    }
  }, [isEnabled, isLoading, options?.redirectIfDisabled, router])

  return {
    isEnabled,
    canToggle: state?.canToggle ?? false,
    source: state?.source ?? "default",
    isLoading
  }
}

/**
 * Convenience hook that returns just the enabled boolean.
 * Use this for simple conditional rendering.
 *
 * @example
 * ```tsx
 * const isCovaMatchEnabled = useIsFeatureEnabled("cova_match")
 *
 * return isCovaMatchEnabled ? <CovaMatchButton /> : null
 * ```
 */
export function useIsFeatureEnabled(key: FeatureFlagKey): boolean {
  const { isFeatureEnabled, isLoading } = useSystemConfiguration()

  // Return false while loading to prevent flash of content
  if (isLoading) return false

  return isFeatureEnabled(key)
}
